/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/no-array-index-key */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import parse from 'autosuggest-highlight/parse';
import throttle from 'lodash/throttle';
import { TextField } from 'components/elements';
import config from 'config';
import { useScript } from 'hooks';

import Styles from './styles';

const useStyles = makeStyles(Styles);

function AddressAutocomplete({ error, helperText, label, onChange, value }) {
  const classes = useStyles();
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);

  const autocompleteService = useRef(null);
  const placesService = useRef(null);

  const scriptStatus = useScript(`https://maps.googleapis.com/maps/api/js?key=${config.googleAPIKey}&libraries=places`);
  useEffect(() => {
    if (!autocompleteService.current && window.google) {
      const map = new window.google.maps.Map(document.createElement('div'));
      placesService.current = new window.google.maps.places.PlacesService(map);
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
  }, [scriptStatus]);

  const fetch = useMemo(
    () =>
      throttle((request, callback) => {
        autocompleteService.current.getPlacePredictions(request, callback);
      }, 200),
    [],
  );

  const fetchDetails = useMemo(
    () =>
      throttle((request, callback, defaultValue) => {
        placesService.current.getDetails(request, (data, status) => {
          if (status === 'OK') {
            callback(data.formatted_address);
          } else {
            callback(defaultValue);
          }
        });
      }, 200),
    [],
  );

  useEffect(() => {
    let active = true;
    if (!autocompleteService.current) return undefined;

    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch({ input: inputValue }, (results) => {
      if (active) {
        let newOptions = [];
        if (value) newOptions = [value];
        if (results) newOptions = [...newOptions, ...results.map((data) => data)];
        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  return (
    <Autocomplete
      data-testid="autocomplete"
      style={{ width: '100%' }}
      id="google-map-demo"
      sx={{ width: 300 }}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value}
      popupIcon={null}
      autoHighlight
      onChange={(event, newValue) => {
        if (!newValue) onChange(newValue);
        else if (typeof newValue === 'string') onChange(newValue);
        else if (newValue.reference) {
          fetchDetails({ reference: newValue.reference }, onChange, newValue.formatted_address);
        } else {
          onChange(newValue);
        }
      }}
      onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
      renderInput={(params) => <TextField data-testid="autocomplete-input" error={error} helperText={helperText} label={label} {...params} />}
      renderOption={(props, option) => {
        const matches = option?.structured_formatting?.main_text_matched_substrings ?? [];
        const parts = parse(
          option?.structured_formatting?.main_text,
          matches.map((match) => [match.offset, match.offset + match.length]),
        );

        return (
          <li {...props}>
            <Grid container alignItems="center">
              <Grid item>
                <Box sx={{ color: 'text.secondary', mr: 2 }}>
                  <LocationOnIcon className={classes.icon} />
                </Box>
              </Grid>
              <Grid item xs>
                {parts.map((part, index) => (
                  <span
                    key={index}
                    style={{
                      fontWeight: part.highlight ? 700 : 400,
                    }}
                  >
                    {part.text}
                  </span>
                ))}

                <Typography variant="body2" color="text.secondary">
                  {option?.structured_formatting?.secondary_text}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
}

AddressAutocomplete.propTypes = {
  error: PropTypes.bool,
  helperText: PropTypes.string,
  label: PropTypes.node.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.objectOf(PropTypes.any)]),
  onChange: PropTypes.func.isRequired,
};

AddressAutocomplete.defaultProps = {
  error: false,
  helperText: '',
  value: '',
};

export default AddressAutocomplete;
